<script setup lang="ts">
interface LabeledSliderProperties {
  min?: number;
  max?: number;
  step?: number;
  title?: string;
  // Read before the title but not shown, when the visible title alone would not tell two sliders apart
  hiddenTitlePrefix?: string;
  disabled?: boolean;
  size?: 's' | 'm';
  formatValue?: (value: number) => string;
}

interface LabeledSliderRange {
  start: number;
  end: number;
}

const properties = withDefaults(defineProps<LabeledSliderProperties>(), {
  min: 0,
  max: 100,
  step: 1,
  title: undefined,
  hiddenTitlePrefix: undefined,
  disabled: false,
  size: 's',
  formatValue: undefined,
});

const isRange = (value: LabeledSliderRange | number): value is LabeledSliderRange => typeof value === 'object';

const textSizeClass = computed(() => (properties.size === 'm' ? 'text-sm' : 'text-xs'));

const model = defineModel<LabeledSliderRange | number>({ required: true });

const thumbLabelPrefix = computed(() =>
  properties.title ? `${[properties.hiddenTitlePrefix, properties.title].filter(Boolean).join(' ')} ` : '',
);

defineSlots<{ labels(preview: LabeledSliderRange): unknown }>();

const sliderValue = computed({
  get(): number[] {
    const v = model.value;
    if (isRange(v)) {
      return [v.start, v.end];
    }
    return [v];
  },
  set(value: number[]) {
    model.value = isRange(model.value) ? { start: value[0] ?? 0, end: value[1] ?? 0 } : (value[0] ?? 0);
  },
});

// displayValue drives the thumb during drag; the parent model is only written on valueCommit
// (end of interaction) to avoid reactive recomputations on every pixel of movement.
// The watch re-syncs displayValue when the model is reset externally (e.g. resetFilters()).
const displayValue = ref<number[]>(sliderValue.value);

const titleId = useId();

watch(sliderValue, (v) => {
  displayValue.value = v;
});
</script>

<template>
  <div class="flex flex-col gap-1">
    <span
      v-if="title"
      :id="titleId"
      class="font-normal transition-colors"
      :class="[textSizeClass, disabled ? 'text-gray-400' : 'text-foreground']"
    >
      <span
        v-if="hiddenTitlePrefix"
        class="sr-only"
        >{{ hiddenTitlePrefix }}</span
      >
      {{ title }}
    </span>

    <UiSlider
      v-model="displayValue"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :aria-labelledby="title ? titleId : undefined"
      :thumb-value-texts="formatValue ? displayValue.map(formatValue) : undefined"
      :thumb-labels="
        isRange(model)
          ? [
              `${thumbLabelPrefix}${$t('components.common.minimum')}`,
              `${thumbLabelPrefix}${$t('components.common.maximum')}`,
            ]
          : undefined
      "
      @value-commit="sliderValue = $event"
    />

    <div
      v-if="$slots.labels"
      :class="[
        textSizeClass,
        'flex justify-between whitespace-nowrap font-normal transition-colors',
        disabled ? 'text-gray-400' : 'text-gray-600',
      ]"
    >
      <slot
        name="labels"
        :start="displayValue[0] ?? min"
        :end="displayValue.at(-1) ?? max"
      />
    </div>
  </div>
</template>
